import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Rating,
  Tabs,
  Tab
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  Chat,
  Speed,
  Star,
  Percent,
  Warning,
  Refresh,
  GetApp,
  Close as CloseIcon
} from '@mui/icons-material';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';

import MetricCard from '../Common/MetricCard';
import { analyticsService, DashboardSummary, AlertMetric, TrendPoint, CsatFeedbackSummary } from '../../services/analyticsService';
import ExportDialog from './ExportDialog';
import ReportBuilder from './ReportBuilder';
import RealTimeAnalytics from './RealTimeAnalytics';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary | null>(null);
  const [alerts, setAlerts] = useState<AlertMetric[]>([]);
  const [conversationTrends, setConversationTrends] = useState<TrendPoint[]>([]);
  const [leadTrends, setLeadTrends] = useState<TrendPoint[]>([]);
  const [csatSummary, setCsatSummary] = useState<CsatFeedbackSummary | null>(null);
  const [dateRange, setDateRange] = useState('7'); // Last 7 days
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [reportBuilderOpen, setReportBuilderOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load dashboard summary
      const summary = await analyticsService.getDashboardSummary();
      setDashboardSummary(summary);

      // Load alerts
      const alertsData = await analyticsService.getActiveAlerts();
      setAlerts(alertsData);

      // Load trends
      const range = analyticsService.getLastNDaysRange(parseInt(dateRange));
      const [convTrends, leadTrendsData, csat] = await Promise.all([
        analyticsService.getPerformanceTrends('conversations', range.start_date, range.end_date),
        analyticsService.getPerformanceTrends('leads', range.start_date, range.end_date),
        analyticsService.getCsatFeedback(range.start_date, range.end_date, 50),
      ]);

      setConversationTrends(convTrends);
      setLeadTrends(leadTrendsData);
      setCsatSummary(csat);

    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError('Error al cargar los datos de analíticas');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveAlert = async (alertId: number) => {
    try {
      await analyticsService.resolveAlert(alertId);
      setAlerts(alerts.filter(alert => alert.id !== alertId));
    } catch (err) {
      console.error('Error resolving alert:', err);
    }
  };

  const getChartData = (trends: TrendPoint[], label: string, color: string) => ({
    labels: trends.map(point => new Date(point.date).toLocaleDateString()),
    datasets: [
      {
        label,
        data: trends.map(point => point.value),
        borderColor: color,
        backgroundColor: `${color}20`,
        fill: true,
        tension: 0.4,
      },
    ],
  });

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const csatChartData = csatSummary ? (() => {
    const byScore = new Map(csatSummary.ratings.map((r) => [r.score, r.count]));
    const labels = ['5', '4', '3', '2', '1'];
    const data = [5, 4, 3, 2, 1].map((s) => byScore.get(s) ?? 0);

    return {
      labels,
      datasets: [
        {
          label: 'Respuestas',
          data,
          backgroundColor: ['#2e7d32', '#66bb6a', '#fbc02d', '#fb8c00', '#d32f2f'],
          borderRadius: 8,
          maxBarThickness: 60,
        },
      ],
    };
  })() : null;

  const csatAverage = csatSummary ? (() => {
    let total = 0;
    let weighted = 0;
    for (const r of csatSummary.ratings) {
      total += r.count;
      weighted += r.score * r.count;
    }
    if (total <= 0) return { avg: 0, total: 0 };
    return { avg: weighted / total, total };
  })() : { avg: 0, total: 0 };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="fade-in">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadAnalyticsData} startIcon={<Refresh />}>
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box className="fade-in">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Analíticas y Reportes
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Visualiza métricas y estadísticas del sistema
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={dateRange}
              label="Período"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="7">Últimos 7 días</MenuItem>
              <MenuItem value="30">Últimos 30 días</MenuItem>
              <MenuItem value="90">Últimos 90 días</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Actualizar datos">
            <IconButton onClick={loadAnalyticsData}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title="Exportar datos">
            <IconButton onClick={() => setExportDialogOpen(true)}>
              <GetApp />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<GetApp />}
            onClick={() => setReportBuilderOpen(true)}
            sx={{ ml: 1 }}
          >
            Crear Reporte
          </Button>
        </Box>
      </Box>

      {/* Tabs for different analytics views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Satisfaccion Cliente" />
          <Tab label="Tasa de Conversion" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 1 ? (
        <RealTimeAnalytics />
      ) : (
        <>
          {/* CSAT Feedback */}
          {csatSummary && (
            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Satisfaccion
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Rating value={csatAverage.avg} precision={0.1} readOnly />
                    <Typography variant="body2" fontWeight={600}>
                      {csatAverage.avg.toFixed(1)} de 5
                    </Typography>
                  </Box>
                  {csatChartData ? (
                    <Bar
                      data={csatChartData}
                      options={{
                        responsive: true,
                        indexAxis: 'y',
                        plugins: { legend: { display: false } },
                        scales: {
                          x: { beginAtZero: true, ticks: { precision: 0 } },
                        },
                      }}
                    />
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="260px">
                      <Typography color="text.secondary">No hay datos disponibles</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Comentarios
                  </Typography>
                  <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                    {csatSummary.items.length === 0 ? (
                      <Typography color="text.secondary">No hay comentarios en este periodo</Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {csatSummary.items.map((item) => {
                          const displayName =
                            (item.customer_name && item.customer_name.trim()) ||
                            (item.customer_phone && item.customer_phone.trim()) ||
                            item.user_id;

                          const dateText = new Date(item.commented_at || item.answered_at).toLocaleString();

                          return (
                            <Grid item xs={12} key={item.id}>
                              <Card variant="outlined">
                                <CardContent sx={{ py: 1.5 }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2">{displayName}</Typography>
                                    <Chip
                                      size="small"
                                      icon={<Star fontSize="small" />}
                                      label={`${item.score}/5`}
                                      color={item.score >= 4 ? 'success' : item.score === 3 ? 'warning' : 'error'}
                                      variant="outlined"
                                    />
                                  </Box>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {item.comment}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                    {dateText}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Alerts Section */}
      {alerts.length > 0 && (
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Alertas Activas
          </Typography>
          <Grid container spacing={2}>
            {alerts.map((alert) => (
              <Grid item xs={12} key={alert.id}>
                <Alert
                  severity={alert.severity === 'critical' ? 'error' : 
                           alert.severity === 'high' ? 'warning' : 'info'}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => handleResolveAlert(alert.id)}
                    >
                      Resolver
                    </Button>
                  }
                >
                  <Typography variant="subtitle2">{alert.title}</Typography>
                  <Typography variant="body2">{alert.description}</Typography>
                </Alert>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

 

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        defaultExportType="analytics"
        title="Exportar Datos de Analytics"
      />

      {/* Report Builder Dialog */}
      <Dialog
        open={reportBuilderOpen}
        onClose={() => setReportBuilderOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Constructor de Reportes</Typography>
            <IconButton onClick={() => setReportBuilderOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <ReportBuilder />
        </DialogContent>
      </Dialog>
        </>
      )}
    </Box>
  );
};

export default Analytics;
